import { type FunctionComponent } from 'react';
import { MAX_DEFAULT_INVENTORY_QUANTITY, MIN_INVENTORY_QUANTITY } from '../../../constants/restricted-inventory';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { ClickableElement } from '../../buttons';
import AddIcon from '../../svgs/add.svg';
import DeleteBinIcon from '../../svgs/delete-bin.svg';
import RemoveIcon from '../../svgs/remove.svg';
import { TextInput } from '../input/input.component';
import { userSelectNone } from './quantity-input.css';

const COUNT_BUTTON_STYLE = 'w-third flex items-center justify-center pointer';

export type QuantityInputEventType = 'INCREMENT' | 'DECREMENT' | 'CHANGE' | 'BLUR' | 'ENTER';

export const QUANTITY_INPUT_STYLE_MAP = {
	DEFAULT: { background: '', color: 'theme-grey-darker', hover: 'hover-bg-theme-grey-light' },
	PRIMARY: { background: 'bg-theme-primary', color: 'theme-white', hover: 'hover-bg-theme-primary-darker' }
};

type QuantityStyle = keyof typeof QUANTITY_INPUT_STYLE_MAP;

export type QuantityInputProps = {
	automationHook?: string;
	label?: string;
	minQuantity?: number;
	maxQuantity?: number;
	defaultQuantity?: number; // Quantity to default to when quantity is invalid on blur
	quantity: number | undefined;
	setQuantity: (quantity: number | undefined, event: QuantityInputEventType) => void | React.Dispatch<React.SetStateAction<number>>;
	disabled?: boolean;
	style?: QuantityStyle;
	canDelete?: boolean;
	isSettled?: boolean;
};

export const QuantityInput: FunctionComponent<QuantityInputProps> = ({
	automationHook,
	label = 'quantity input',
	minQuantity = MIN_INVENTORY_QUANTITY,
	defaultQuantity = MIN_INVENTORY_QUANTITY,
	quantity,
	setQuantity,
	disabled,
	style = 'DEFAULT',
	canDelete = false,
	isSettled,
	maxQuantity = MAX_DEFAULT_INVENTORY_QUANTITY
}) => {
	const quantityNumber = Number(quantity);
	// #region Event Handlers
	const incrementQuantity = () => {
		if (quantityNumber >= maxQuantity) {
			return setQuantity(maxQuantity, 'INCREMENT');
		}
		setQuantity(quantityNumber + 1, 'INCREMENT');
	};

	const decrementQuantity = () => {
		if (quantityNumber <= minQuantity) {
			return setQuantity(minQuantity, 'DECREMENT');
		}
		setQuantity(quantityNumber - 1, 'DECREMENT');
	};

	const handleQuantityBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		// When we lose focus on the field and the value is invalid default it to 1
		setQuantity(!isNaN(value) ? Math.min(value, maxQuantity) : defaultQuantity, 'BLUR');
	};

	const handleQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
		// need to cache this quantity since setState is async another option is
		// using event.persist() could risk browser compatibility
		event.preventDefault();
		let newValue = Math.abs(parseInt(event.target.value, 10));
		if (!isNaN(newValue)) {
			// value should not be larger than MAX_QUANTITY
			newValue = Math.min(newValue, maxQuantity);
			// value should not be smaller than minQuantity
			newValue = Math.max(newValue, minQuantity);
			setQuantity(newValue, 'CHANGE');
		} else {
			// if value cannot parse an int set quantity to undefined so users can still enter single digit number 1-9
			setQuantity(undefined, 'CHANGE');
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		if (event.key === 'Enter') {
			setQuantity(quantity, 'ENTER');
		} else if (event.key === 'ArrowUp') {
			incrementQuantity();
		} else if (event.key === 'ArrowDown') {
			decrementQuantity();
		}
	};
	// #endregion

	const chosenStyle = QUANTITY_INPUT_STYLE_MAP[style];

	return (
		<div className={`flex ba br2 justify-between theme-grey-light ${quantityNumber > 0 ? '' : isSettled ? 'ba b--theme-error' : ''}`}>
			<ClickableElement
				ariaLabel="decrease quantity"
				className={`br2 br--left ${COUNT_BUTTON_STYLE} ${chosenStyle.color} ${chosenStyle.background} ${chosenStyle.hover} ${userSelectNone}`}
				onClick={decrementQuantity}
				onKeyDown={handleKeys(['Enter', ' '], decrementQuantity)}
				testId="decrease-quantity"
				automationHook={automationHook ? `${automationHook}-decrease` : undefined}
				disabled={disabled}>
				{canDelete && quantity === 1 ? (
					<DeleteBinIcon className={`f3 ${chosenStyle.color}`} data-testid="DeleteBinIcon" />
				) : (
					<RemoveIcon className={`f3 ${chosenStyle.color}`} />
				)}
			</ClickableElement>
			<div className="w-third">
				<TextInput
					ariaLabel={label}
					testId="input-quantity"
					omitBaseClasses={true}
					className={`input-reset bn f5 flex items-center ${chosenStyle.background} theme-grey-darker ${
						quantityNumber > 0 ? '' : 'outline-0'
					}`}
					inputClassName="tc"
					value={quantity ?? ''}
					onBlur={handleQuantityBlur}
					onChange={handleQuantity}
					onKeyDown={handleKeys(['Enter', 'ArrowUp', 'ArrowDown'], handleKeyDown)}
					automationHook={automationHook}
					inputMode="numeric" // bring up numeric keyboard
					pattern="\d*" // REGEX: any number; by defining this pattern older iOS devices will bring up a numeric keyboard
					disabled={disabled}
				/>
			</div>
			<ClickableElement
				ariaLabel="increase quantity"
				className={`br2 br--right ${chosenStyle.color} ${chosenStyle.background} ${chosenStyle.hover} ${COUNT_BUTTON_STYLE} ${userSelectNone}`}
				onClick={incrementQuantity}
				onKeyDown={handleKeys(['Enter', ' '], incrementQuantity)}
				testId="increase-quantity"
				automationHook={automationHook ? `${automationHook}-increase` : undefined}
				disabled={disabled}>
				<AddIcon className={`f3 ${chosenStyle.color}`} data-testid="AddIcon" />
			</ClickableElement>
		</div>
	);
};

export type DynamicQuantityInputProps = {
	text: string;
} & QuantityInputProps;

export const DynamicQuantityInput: FunctionComponent<DynamicQuantityInputProps> = (props) => {
	const containerStyle: React.CSSProperties = { width: '150px', height: '46px', borderRadius: '5px' };

	return (
		<>
			{props.quantity === 0 ? (
				<div className="shadow-secondary fw7 bn theme-primary-dark active-theme-primary-darker" style={containerStyle}>
					<ClickableElement
						className="w-100 h-100 flex justify-center items-center"
						ariaLabel="open quantity selector"
						disabled={props.disabled}
						onClick={() => props.setQuantity(Number(props.quantity) + 1, 'INCREMENT')}>
						{props.text}
					</ClickableElement>
				</div>
			) : (
				<div style={containerStyle}>
					<QuantityInput {...props} minQuantity={0} />
				</div>
			)}
		</>
	);
};
